// sound5/voices.js: the sustained layers (organ, choir, halo, drone, tension
// cluster, cloud and air noise). Every pitched oscillator's detune has the
// engine's global `bend` signal connected to it, so one parameter bends the
// whole score (gravitational redshift). Layers create their nodes when they
// wake up and stop them after sleeping a while, so idle layers cost no CPU.
import { mtof, biquad, Knob, makeWave, noiseBuffer, warmCurve, makePan } from './util.js';

// Mix bus: voices -> in [-> pre] -> level -> dry + hall send. `pre` is
// [first, last] of a filter chain the caller has wired internally.
export class Bus {
  constructor(eng, dry, send, pre) {
    const c = eng.ctx;
    this.in = c.createGain();
    this.g = c.createGain();
    this.g.gain.value = 0;
    if (pre) { this.in.connect(pre[0]); pre[pre.length - 1].connect(this.g); } else this.in.connect(this.g);
    if (dry > 0) { const d = c.createGain(); d.gain.value = dry; this.g.connect(d); d.connect(eng.master.dry); }
    if (send > 0) { const s = c.createGain(); s.gain.value = send; this.g.connect(s); s.connect(eng.master.send); }
    this.k = new Knob(this.g.gain, 0, 0.01, 1e-4);
    this.target = 0;
  }
  set(v, t, tc) { this.target = v; this.k.set(v, t, tc); }
}

// A voice: sources that feed one envelope gain. Envelopes only ever use
// setTargetAtTime, so a release can interrupt an attack without a click.
export function voice(srcs, g, t0) {
  return {
    t0, dead: false,
    release(t, rel) {
      if (this.dead) return;
      this.dead = true;
      const tr = Math.max(t, t0);
      try {
        g.gain.setTargetAtTime(0, tr, Math.max(0.01, rel / 4));
        for (const s of srcs) s.stop(tr + rel * 2 + 0.05);
      } catch (e) { /* already stopped */ }
    },
  };
}

// Plays the current chord on one timbre, voicing the notes for its register.
export class ChordLayer {
  constructor(eng, bus, make, pick, tc) {
    Object.assign(this, { eng, bus, make, pick, tc });
    this.voices = [];
    this.notes = null;
    this.on = false;
    this.sleepAt = 0;
    this.atk = 1;
    this.amp = 0.04;
  }
  chord(notes, t, atk, rel, amp) {
    this.notes = this.pick(notes);
    Object.assign(this, { atk, rel, amp });
    this.stop(t, rel);
    if (this.on) this.spawn(t, atk);
  }
  spawn(t, atk) {
    for (const m of this.notes) this.voices.push(this.make(m, t, atk, this.amp));
  }
  stop(t, rel) {
    for (const v of this.voices) v.release(t, rel);
    this.voices = [];
  }
  // wakes when its level target rises, sleeps a few time constants after it falls
  wake(t) {
    const want = this.bus.target > 0.004;
    if (want) {
      this.sleepAt = 0;
      if (!this.on) { this.on = true; if (this.notes) this.spawn(t, Math.max(0.4, this.atk)); }
    } else if (this.on) {
      if (!this.sleepAt) this.sleepAt = t + this.tc * 5;
      else if (t > this.sleepAt) { this.on = false; this.stop(t, 0.6); }
    }
  }
}

export function makeLayers(eng) {
  const c = eng.ctx, cfg = eng.cfg, rng = eng.rng;
  const waves = {};
  const wave = (reg) => waves[reg] || (waves[reg] = makeWave(c, eng.regs[reg] || eng.regs.principal));
  const env = (g, amp, t, atk) => { g.gain.value = 0; g.gain.setTargetAtTime(amp, t, Math.max(0.005, atk / 3)); };
  // every pitched oscillator follows the global bend; `mods` are extra
  // long-lived modulators (the choir vibrato). All are disconnected when the
  // oscillator ends, so nothing keeps dead voices alive.
  const osc = (type, m, t, detune, mods) => {
    const o = c.createOscillator();
    if (typeof type === 'string') o.type = type; else o.setPeriodicWave(type);
    o.frequency.value = mtof(m);
    o.detune.value = detune || 0;
    eng.bendTo(o.detune);
    if (mods) for (const md of mods) md.connect(o.detune);
    o.onended = () => {
      eng.unbend(o.detune);
      if (mods && eng.canUnbend) for (const md of mods) try { md.disconnect(o.detune); } catch (e) { /* ignore */ }
      try { o.disconnect(); } catch (e) { /* ignore */ }
    };
    o.start(t);
    return o;
  };

  // ORGAN: one PeriodicWave oscillator per note, register from the progression
  const organLp = biquad(c, 'lowpass', 2400, 0.6);
  const organ = new Bus(eng, 0.8, 0.42, [organLp]);
  organ.lp = new Knob(organLp.frequency, 2400, 0.02, 5);
  const organL = new ChordLayer(eng, organ, (m, t, atk, amp) => {
    const o = osc(wave(eng.reg), m, t, (rng() - 0.5) * 5);
    const g = c.createGain();
    env(g, amp * (m < 45 ? 0.8 : 1), t, atk);
    o.connect(g); g.connect(organ.in);
    return voice([o], g, t);
  }, (n) => (cfg.organNotes < n.length ? n.slice(0, 1).concat(n.slice(n.length - cfg.organNotes + 1)) : n), 1.2);

  // CHOIR: sawtooth voices through a shared three-formant bank ("oo" -> "ah")
  const f = [biquad(c, 'bandpass', 300, 7), biquad(c, 'bandpass', 870, 9), biquad(c, 'bandpass', 2240, 12)];
  const fin = c.createGain(), fsum = c.createGain(), fAir = biquad(c, 'lowpass', 4200, 0.5);
  const fg = [1, 0.55, 0.22];
  f.forEach((b, i) => { const gg = c.createGain(); gg.gain.value = fg[i] * 3.2; fin.connect(b); b.connect(gg); gg.connect(fsum); });
  fsum.connect(fAir);
  const choir = new Bus(eng, 0.45, 0.85, [fin, fAir]);
  choir.forms = f.map((b) => new Knob(b.frequency, b.frequency.value, 0.01, 1));
  const vib = c.createOscillator(), vibG = c.createGain();
  vib.frequency.value = 4.6; vibG.gain.value = 7; vib.connect(vibG); vib.start();
  const choirL = new ChordLayer(eng, choir, (m, t, atk, amp) => {
    const srcs = [], g = c.createGain();
    for (let k = 0; k < cfg.choirUnison; k++) {
      const o = osc('sawtooth', m, t, (k - (cfg.choirUnison - 1) / 2) * 11 + (rng() - 0.5) * 4, [vibG]);
      o.connect(g);
      srcs.push(o);
    }
    env(g, amp / cfg.choirUnison, t, atk * 1.4);
    g.connect(choir.in);
    return voice(srcs, g, t);
  }, (n) => n.filter((m) => m >= 50 && m <= 81).slice(-cfg.choirNotes), 1.5);

  // HALO: sines an octave above the top of the chord, almost all hall
  const halo = new Bus(eng, 0.12, 1.0);
  const haloL = new ChordLayer(eng, halo, (m, t, atk, amp) => {
    const o = osc('sine', m + 12, t, (rng() - 0.5) * 8), g = c.createGain(), p = makePan(c, rng() * 1.4 - 0.7);
    env(g, amp * 0.9, t, atk * 2);
    o.connect(g); g.connect(p.node); p.node.connect(halo.in);
    return voice([o], g, t);
  }, (n) => n.slice(-cfg.haloNotes), 2);

  // DRONE: D1 saw + D1 sine sub + A1 triangle, low-passed and warmed so the
  // sub still reads on phone speakers; brown-noise "cloud" rumble beside it
  const droneLp = biquad(c, 'lowpass', 110, 1.1), warm = c.createWaveShaper();
  warm.curve = warmCurve();
  droneLp.connect(warm);
  const drone = new Bus(eng, 1, 0.12, [droneLp, warm]);
  drone.lp = new Knob(droneLp.frequency, 110, 0.02, 1);
  drone.rise = 0;
  drone.nodes = null;
  drone.wake = (t) => {
    if (drone.target > 0.004) {
      drone.sleepAt = 0;
      if (drone.nodes) return;
      const saw = osc('sawtooth', 26, t, drone.rise), sub = osc('sine', 26, t, drone.rise), tri = osc('triangle', 33, t, drone.rise);
      const gs = c.createGain(), gb = c.createGain(), gt = c.createGain();
      gs.gain.value = 0.4; gb.gain.value = 0.16; gt.gain.value = 0.22;
      saw.connect(gs); sub.connect(gb); tri.connect(gt);
      gs.connect(drone.in); gt.connect(drone.in); gb.connect(drone.g); // the pure sub skips the filter and the shaper
      drone.nodes = [saw, sub, tri];
      drone.rk = drone.nodes.map((o) => new Knob(o.detune, drone.rise, 0, 0.5));
    } else if (drone.nodes) {
      if (!drone.sleepAt) drone.sleepAt = t + 2.5;
      else if (t > drone.sleepAt) { for (const o of drone.nodes) try { o.stop(t + 0.05); } catch (e) { /* ignore */ } drone.nodes = null; }
    }
  };
  const cloudLp = biquad(c, 'lowpass', 260, 0.7);
  const cloud = new Bus(eng, 1, 0.25, [cloudLp]);
  cloud.lp = new Knob(cloudLp.frequency, 260, 0.02, 1);
  cloud.buf = noiseBuffer(c, 4, 'brown', rng, 0.35, 22050);
  const air = new Bus(eng, 1, 0.2, [biquad(c, 'highpass', 300, 0.6)]);
  air.buf = noiseBuffer(c, 3, 'pink', rng, 0.2, 22050);
  for (const bus of [cloud, air]) {
    bus.src = null;
    bus.wake = (t) => {
      if (bus.target > 0.004) {
        bus.sleepAt = 0;
        if (bus.src) return;
        const s = c.createBufferSource();
        s.buffer = bus.buf; s.loop = true;
        s.connect(bus.in); s.start(t, rng() * 2);
        bus.src = s;
      } else if (bus.src) {
        if (!bus.sleepAt) bus.sleepAt = t + 2.5;
        else if (t > bus.sleepAt) { try { bus.src.stop(t + 0.05); } catch (e) { /* ignore */ } bus.src = null; }
      }
    };
  }
  return { organ, organL, choir, choirL, halo, haloL, drone, cloud, air, vib };
}
